import { useMemo, useState } from 'react'
import { compareTimestamps } from '@ovasabi/frontend-kit'
import * as S from './OpsStyle'
import { formatWhen, useOpsTickets, useProfileNames, type TicketRow } from './opsData'

/**
 * Customer relations: the help-ticket queue, newest first, with the SLA level
 * the billing tier bought the customer visible on the row — a `vip` ticket
 * sitting at the bottom of the list is the thing an agent must not miss.
 *
 * Tickets are read-only here for now. `assist:escalate_help_ticket` and
 * `assist:record_ai_resolution` exist as admin routes, but escalation without a
 * destination queue would be a button that only changes a word in the database;
 * that belongs with the support-routing work, not ahead of it.
 */

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'bad' | 'flat'> = {
  open: 'warn',
  escalated: 'bad',
  ai_resolved: 'flat',
  resolved: 'ok',
  closed: 'flat',
}

const SLA_TONE: Record<string, 'ok' | 'warn' | 'bad' | 'flat'> = {
  vip: 'bad',
  priority: 'warn',
  standard: 'flat',
}

export const OpsSupportPage = () => {
  const tickets = useOpsTickets()
  const names = useProfileNames()
  const [showResolved, setShowResolved] = useState(false)

  const { queue, resolved } = useMemo(() => {
    const isDone = (t: TicketRow) => t.status === 'resolved' || t.status === 'closed'
    return {
      queue: tickets.filter((t) => !isDone(t)),
      resolved: tickets.filter(isDone),
    }
  }, [tickets])

  const rows = showResolved ? resolved : queue

  return (
    <>
      <div>
        <S.H1>Support</S.H1>
      </div>
      <S.Card>
        <S.CardTitle>
          {showResolved ? `Resolved (${resolved.length})` : `Open queue (${queue.length})`}
        </S.CardTitle>
        <S.Nav>
          <S.NavLink variant="quiet" $active={!showResolved} onClick={() => setShowResolved(false)}>
            Open ({queue.length})
          </S.NavLink>
          <S.NavLink variant="quiet" $active={showResolved} onClick={() => setShowResolved(true)}>
            Resolved ({resolved.length})
          </S.NavLink>
        </S.Nav>
        {rows.length === 0 ? (
          <S.Empty>{showResolved ? 'Nothing resolved yet.' : 'The queue is empty.'}</S.Empty>
        ) : (
          <S.TableScroll>
            <S.Table>
              <thead>
                <tr>
                  <th>Raised</th>
                  <th>Customer</th>
                  <th>Subject</th>
                  <th>SLA</th>
                  <th>Channel</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {[...rows]
                  .sort((a, b) => {
                    const rank = (t: TicketRow) =>
                      t.slaLevel === 'vip' ? 0 : t.slaLevel === 'priority' ? 1 : 2
                    const bySla = rank(a) - rank(b)
                    return bySla !== 0
                      ? bySla
                      : compareTimestamps(a.createdAt, b.createdAt, a.id, b.id, { direction: 'desc' })
                  })
                  .map((ticket) => (
                    <tr key={ticket.id}>
                      <td>{formatWhen(ticket.createdAt)}</td>
                      <td>{names.get(ticket.profileId) ?? '—'}</td>
                      <td className="wrap">
                        <strong>{ticket.subject}</strong>
                        {ticket.body && (
                          <>
                            <br />
                            {ticket.body.slice(0, 160)}
                            {ticket.body.length > 160 ? '…' : ''}
                          </>
                        )}
                      </td>
                      <td>
                        <S.Pill $tone={SLA_TONE[ticket.slaLevel] ?? 'flat'}>{ticket.slaLevel}</S.Pill>
                      </td>
                      <td>{ticket.channel.replace(/_/g, ' ')}</td>
                      <td>
                        <S.Pill $tone={STATUS_TONE[ticket.status] ?? 'flat'}>
                          {ticket.status.replace(/_/g, ' ')}
                        </S.Pill>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </S.Table>
          </S.TableScroll>
        )}
      </S.Card>
    </>
  )
}
